import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { getTranslation, getTranslationArr, logError } from "../helpers/utils";
import { guidanceStepServesDistrict } from "../helpers/locationHelpers";

/** When GET /guidance-step/:district returns [], re-filter full guidance client-side (parity with backend rules). */
function filterGuidanceOffencesByDistrict(allOffences, districtId, provinceId) {
  return (allOffences || [])
    .map((offence) => {
      const steps = (offence.guidance_steps || []).filter((step) =>
        guidanceStepServesDistrict(step, districtId, provinceId),
      );
      return { ...offence, guidance_steps: steps };
    })
    .filter((o) => (o.guidance_steps || []).length > 0);
}

export const getLaws = createAsyncThunk(
  "OffenceSlice/getLaws",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/laws`
      );
      return {
        res: response.data.data["Relevant Laws"],
        langId: data.langId,
      };
    } catch (error) {
      logError(error, "OffenceSlice");
      return rejectWithValue(error.response.data);
    }
  }
);

export const getSelectedLaw = createAsyncThunk(
  "OffenceSlice/getSelectedLaw",
  async (data, { rejectWithValue, getState }) => {
    try {
      // Guard against undefined id
      if (!data.id) {
        console.warn('[getSelectedLaw] Missing id:', data);
        return { res: [], langId: data.langId };
      }
      
      // Check cache first
      const state = getState();
      const cacheKey = `law_${data.id}`;
      const cached = state.offence.lawsCache?.[cacheKey];
      if (cached) {
        if (process.env.NODE_ENV === 'development') console.log('[OffenceSlice] Using cached law for:', data.id);
        return { res: cached, langId: data.langId, cacheKey, fromCache: true };
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/laws/${data.id}`
      );
      return {
        res: response.data.data["Relevant Law"],
        langId: data.langId,
        cacheKey,
      };
    } catch (error) {
      logError(error, "OffenceSlice");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const hasDistrict = (d) => d != null && String(d).trim() !== "" && String(d) !== "undefined";

export const getGuidance = createAsyncThunk(
  "OffenceSlice/getGuidance",
  async (data, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const district = hasDistrict(data.district) ? data.district : null;

      if (district) {
        const cacheKey = `guidance_${district}`;
        const cached = state.offence.guidanceCache?.[cacheKey];
        if (cached && Array.isArray(cached) && cached.length > 0) {
          if (process.env.NODE_ENV === 'development') console.log('[OffenceSlice] Using cached guidance for:', district);
          return { res: cached, langId: data.langId, cacheKey, fromCache: true };
        }
        const provinceId =
          data.provinceId != null && data.provinceId !== ""
            ? data.provinceId
            : state.province?.selProvinceId;

        const response = await axios.get(
          `${process.env.REACT_APP_LOCAL_API}/guidance-step/${district}`
        );
        let listRaw =
          response.data?.data?.["Guidance Step"] ??
          response.data?.data?.["Guidance step"] ??
          [];
        listRaw = Array.isArray(listRaw) ? listRaw : [];

        if (listRaw.length === 0) {
          let allRaw = state.offence.guidanceCache?.["guidance_all"];
          let fetchedAllFromApi = false;
          if (!allRaw || !Array.isArray(allRaw)) {
            const responseAll = await axios.get(
              `${process.env.REACT_APP_LOCAL_API}/guidance-step`
            );
            allRaw =
              responseAll.data?.data?.["Guidance Step"] ??
              responseAll.data?.data?.["Guidance step"] ??
              [];
            allRaw = Array.isArray(allRaw) ? allRaw : [];
            fetchedAllFromApi = true;
          }
          listRaw = filterGuidanceOffencesByDistrict(
            allRaw,
            district,
            provinceId
          );
          return {
            res: listRaw,
            langId: data.langId,
            cacheKey,
            seedGuidanceAll: fetchedAllFromApi ? allRaw : undefined,
          };
        }

        return {
          res: listRaw,
          langId: data.langId,
          cacheKey,
        };
      }

      // No district: full guidance list (same as GET /guidance-step). Needed when selDistrictId was cleared or not set yet.
      const cacheKey = "guidance_all";
      const cachedAll = state.offence.guidanceCache?.[cacheKey];
      if (cachedAll) {
        if (process.env.NODE_ENV === 'development') console.log('[OffenceSlice] Using cached guidance (all)');
        return { res: cachedAll, langId: data.langId, cacheKey, fromCache: true };
      }
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/guidance-step`
      );
      const rawAll =
        response.data?.data?.["Guidance Step"] ??
        response.data?.data?.["Guidance step"] ??
        [];
      return {
        res: Array.isArray(rawAll) ? rawAll : [],
        langId: data.langId,
        cacheKey,
      };
    } catch (error) {
      logError(error, "OffenceSlice");
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (typeof error?.response?.data === "string"
          ? error.response.data
          : null) ||
        error?.message ||
        "Could not load guidance";
      return rejectWithValue(msg);
    }
  }
);

export const getSelectedGuidance = createAsyncThunk(
  "OffenceSlice/getSelectedGuidance",
  async (data, { rejectWithValue, getState }) => {
    try {
      if (!data.id) {
        console.warn('[getSelectedGuidance] Missing id:', data);
        return { res: [], langId: data.langId };
      }

      const district = hasDistrict(data.district) ? data.district : null;

      if (!district) {
        const state = getState();
        let allRaw = state.offence.guidanceCache?.["guidance_all"];
        let fetchedAll = false;
        if (!allRaw || !Array.isArray(allRaw)) {
          const response = await axios.get(
            `${process.env.REACT_APP_LOCAL_API}/guidance-step`
          );
          const list =
            response.data?.data?.["Guidance Step"] ??
            response.data?.data?.["Guidance step"] ??
            [];
          allRaw = Array.isArray(list) ? list : [];
          fetchedAll = true;
        }
        const found = (allRaw || []).find(
          (o) => String(o._id) === String(data.id)
        );
        if (!found) {
          console.warn("[getSelectedGuidance] Offence not found in guidance list:", data.id);
          return { res: [], langId: data.langId };
        }
        return {
          res: [found],
          langId: data.langId,
          seedGuidanceAll: fetchedAll ? allRaw : undefined,
        };
      }

      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/one-guidance-step/${data.id}/${district}`
      );
      const oneRaw =
        response.data?.data?.["Guidance Step"] ??
        response.data?.data?.["Guidance step"] ??
        [];
      return {
        res: Array.isArray(oneRaw) ? oneRaw : oneRaw ? [oneRaw] : [],
        langId: data.langId,
      };
    } catch (error) {
      logError(error, "OffenceSlice");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const OffenceSlice = createSlice({
  name: "offence",
  initialState: {
    offenceLaws: [],
    offenceGuidance: [],
    selectedLaw: null,
    selectedGuide: null,
    lawsCache: {},      // Cache laws per offence ID
    guidanceCache: {},  // Cache guidance per district ID
    guidanceLoading: false,
    guidanceLoadError: null,
  },
  reducers: {
    setSelectedLaw: (state, action) => {
      state.selectedLaw = action.payload;
    },
    setSelectedGuide: (state, action) => {
      state.selectedGuide = action.payload;
    },
    resetOffStates: (state, _action) => {
      state.offenceLaws = [];
      state.offenceGuidance = [];
      state.selectedLaw = null;
      state.selectedGuide = {};
      state.guidanceLoading = false;
      state.guidanceLoadError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLaws.fulfilled, (state, action) => {
        state.offenceLaws = action.payload.res.map((obj) => {
          let trans = { ...obj };
          trans.title = getTranslation(obj.title, action.payload.langId);
          trans.description = getTranslation(
            obj.description,
            action.payload.langId
          );
          return trans;
        });
      })
      .addCase(getLaws.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(getSelectedLaw.fulfilled, (state, action) => {
        const raw = action.payload.res;
        // Normalize: backend may return array or single object; cache stores as-is
        if (!action.payload.fromCache && action.payload.cacheKey) {
          state.lawsCache[action.payload.cacheKey] = raw;
        }
        const list = Array.isArray(raw) ? raw : (raw != null ? [raw] : []);
        const law = list.map((obj) => {
          let trans = { ...obj };
          trans.title = getTranslation(obj?.title, action.payload.langId);
          trans.description = getTranslation(
            obj?.description,
            action.payload.langId
          );
          trans.laws = obj?.laws?.map((lawItem) => {
            let transLaw = { ...lawItem };
            transLaw.title = getTranslation(lawItem?.title, action.payload.langId);
            return transLaw;
          }) || [];
          trans.sections = getTranslationArr(obj?.sections, action.payload.langId);
          return trans;
        });
        state.selectedLaw = law[0] ?? null;
      })
      .addCase(getSelectedLaw.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(getGuidance.pending, (state) => {
        state.guidanceLoading = true;
        state.guidanceLoadError = null;
      })
      .addCase(getGuidance.fulfilled, (state, action) => {
        state.guidanceLoading = false;
        state.guidanceLoadError = null;
        if (action.payload.seedGuidanceAll) {
          state.guidanceCache["guidance_all"] = action.payload.seedGuidanceAll;
        }
        const listRaw = Array.isArray(action.payload.res) ? action.payload.res : [];
        // Cache the raw response
        if (!action.payload.fromCache && action.payload.cacheKey) {
          state.guidanceCache[action.payload.cacheKey] = listRaw;
        }

        state.offenceGuidance = listRaw.map((obj) => {
          let trans = { ...obj };
          trans.title = getTranslation(obj.title, action.payload.langId);
          trans.description = getTranslation(
            obj.description,
            action.payload.langId
          );
          return trans;
        });
      })
      .addCase(getGuidance.rejected, (state, action) => {
        state.guidanceLoading = false;
        state.guidanceLoadError = action.payload ?? "Unknown error";
        const msg =
          typeof action.payload === "string"
            ? action.payload
            : action.payload?.message || "Could not load guidance";
        toast.error(msg);
      })
      .addCase(getSelectedGuidance.fulfilled, (state, action) => {
        if (action.payload.seedGuidanceAll) {
          state.guidanceCache["guidance_all"] = action.payload.seedGuidanceAll;
        }
        const raw = action.payload.res;
        const resList = Array.isArray(raw)
          ? raw
          : raw != null
            ? [raw]
            : [];
        if (resList.length === 0) {
          console.warn('[getSelectedGuidance] No data returned');
          state.selectedGuide = null;
          return;
        }

        const guides = resList.map((obj) => {
          let trans = { ...obj };
          trans.title = getTranslation(obj.title, action.payload.langId);
          trans.description = getTranslation(
            obj.description,
            action.payload.langId
          );
          trans.guidance_steps = (obj.guidance_steps || []).map((step) => {
            let newStep = { ...step };
            newStep.title = getTranslation(step.title, action.payload.langId);
            // Preserve video_url field
            newStep.video_url = step.video_url;
            newStep.guidance_step_description =
              (step.guidance_step_description || []).map((stepDes) => {
                let desObj = { ...stepDes };
                desObj.description = getTranslation(
                  stepDes.description,
                  action.payload.langId
                );
                desObj.heading = getTranslation(
                  stepDes.heading,
                  action.payload.langId
                );
                return desObj;
              });
            return newStep;
          });
          return trans;
        });
        state.selectedGuide = guides[0] || null;
      })
      .addCase(getSelectedGuidance.rejected, (state, action) => {
        const msg =
          typeof action.payload === "string"
            ? action.payload
            : action.payload?.message || "Could not load this guidance topic";
        toast.error(msg);
      });
  },
});
export const { setSelectedLaw, setSelectedGuide, resetOffStates } =
  OffenceSlice.actions;

export default OffenceSlice.reducer;
